import type { CheckHealthResponse } from "@harmonyso/tenants-sdk";
import { PRIMARY_CELL, isRemoteCell, type Cell } from "./cells";
import { isCellSignInError } from "./cell-session";
import type { CellFailure } from "./cell-tenants";

/** The heading for a cell whose health couldn't be checked. */
export const healthUnknown = (cell: Cell) =>
  `${cell.id} (${cell.residency}) health is unknown`;

export interface CellHealthResult {
  cell: Cell;
  health?: CheckHealthResponse;
  error?: Error | null;
  isLoading: boolean;
}

/** Another cell whose health wasn't asked for, because it has no session. */
export const isUncheckedCell = ({ cell, error }: CellHealthResult) =>
  isRemoteCell(cell) && !!error && isCellSignInError(error);

/**
 * Every cell's health, plus the cells that couldn't be reached. `primary` is
 * the primary cell's result. `allHealthy` covers the cells that were checked:
 * a cell without a session isn't counted as unhealthy.
 */
export function combineCellHealth(results: CellHealthResult[]) {
  const failures: CellFailure[] = results.flatMap(({ cell, error }) =>
    error ? [{ cell, error }] : [],
  );
  const isLoading = results.some((result) => result.isLoading);
  const checked = results.filter((result) => !isUncheckedCell(result));
  return {
    cells: results,
    primary: results.find(({ cell }) => cell.id === PRIMARY_CELL.id),
    failures,
    isLoading,
    allHealthy:
      !isLoading &&
      checked.length > 0 &&
      checked.every((result) => result.health?.status === "healthy"),
  };
}
